import { useCallback, useMemo, useRef, useState } from 'react';

import set from 'lodash/set';

import {
  getScheduledTransactionEditValidationResult,
  type ScheduledTransactionEditFormValues,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionEditForm';

import type { ScheduledTransactionEditFormValidation } from '../types';
import { scheduledTransactionEditFormHooks } from './formHooks';

type Params = {
  setTabValue: (value: number) => void;
};

type Result = {
  handleFieldChange: (path: string) => void;
  handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
  submit: (force?: boolean) => Promise<void>;
  validation: ScheduledTransactionEditFormValidation;
};

export function useScheduledTransactionEditFormSubmit({ setTabValue }: Params): Result {
  const manager = scheduledTransactionEditFormHooks.useManager();
  const currentDate = scheduledTransactionEditFormHooks.useSelector(({ state }) => state.currentDate);
  const initialDueOn = scheduledTransactionEditFormHooks.useSelector(({ state }) => state.initialDueOn);
  const didTrySubmitRef = useRef(false);

  const [errors, setErrorsState] = useState<Record<string, any>>({});
  const [touched, setTouchedState] = useState<Record<string, any>>({});
  const [_isSubmitting, setIsSubmitting] = useState(false);

  const setFieldError = useCallback((path: string, error: string | undefined) => {
    setErrorsState((current) => {
      const next = { ...current };
      set(next, path, error);
      return next;
    });
  }, []);

  const validateValues = useCallback(
    (valuesToValidate: ScheduledTransactionEditFormValues, force = false) =>
      getScheduledTransactionEditValidationResult({
        values: valuesToValidate,
        initialDueOn,
        currentDate,
        force,
      }),
    [currentDate, initialDueOn],
  );

  const handleFieldChange = useCallback(
    (path: string) => {
      setTouchedState((current) => {
        const next = { ...current };
        set(next, path, true);
        return next;
      });

      if (didTrySubmitRef.current) {
        const currentValues = manager.runSelector(({ state }) => state.values);
        setErrorsState(validateValues(currentValues).errors);
      }
    },
    [manager, validateValues],
  );

  const submit = useCallback(
    async (force = false) => {
      didTrySubmitRef.current = true;
      setIsSubmitting(true);

      const currentValues = manager.runSelector(({ state }) => state.values);
      const validation = validateValues(currentValues, force);
      setErrorsState(validation.errors);

      if (validation.tabValue !== undefined) {
        setTabValue(validation.tabValue);
        setIsSubmitting(false);
        return;
      }

      await manager.submit({ force });
      setIsSubmitting(false);
    },
    [manager, setTabValue, validateValues],
  );

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      event?.stopPropagation();
      void submit(false);
    },
    [submit],
  );

  const validation = useMemo<ScheduledTransactionEditFormValidation>(
    () => ({
      errors,
      touched,
      setFieldError,
      setErrors: setErrorsState,
      setSubmitting: setIsSubmitting,
    }),
    [errors, setFieldError, touched],
  );

  return {
    handleFieldChange,
    handleSubmit,
    submit,
    validation,
  };
}
